export type AdminRecord = Record<string, any>;

export interface SiteSettings {
  logoUrl: string;
  siteBackgroundUrl: string;
  heroBackgroundUrl: string;
  fontFamily: 'tajawal' | 'cairo' | 'noto-kufi' | 'system';
  hideOutOfStockProducts: boolean;
  whatsapp: string;
  facebook: string;
  instagram: string;
  threads: string;
  telegram: string;
  branches: Array<{ name: string; address: string; phone: string; mapUrl: string }>;
}

export interface AdminWorkspace {
  sessionUserId: string;
  sessionRole: string;
  sessionPermissions: string[];
  permissionCatalog: Array<{ id: string; label: string; group: string }>;
  summary: {
    todaySales: number;
    orders: number;
    products: number;
    customers: number;
    stock: number;
    lowStock: number;
    outOfStock: number;
    hiddenProducts: number;
  };
  products: AdminRecord[];
  categories: AdminRecord[];
  orders: AdminRecord[];
  customers: AdminRecord[];
  inventory: { stock: AdminRecord[]; movements: AdminRecord[] };
  payments: AdminRecord[];
  shipping: AdminRecord[];
  banners: AdminRecord[];
  coupons: AdminRecord[];
  users: AdminRecord[];
  roles: AdminRecord[];
  pages: AdminRecord[];
  settings: SiteSettings;
  analytics: {
    totals: { daily: number; monthly: number; yearly: number };
    byDay: Array<{ day: string; sales: number }>;
    topProducts: AdminRecord[];
    topCustomers: AdminRecord[];
  };
}

export interface SalesReport {
  range: { from: string; to: string; timeZone: string };
  summary: { sales: number; orders: number; cancelledOrders: number; newCustomers: number; items: number; averageOrder: number };
  byDay: Array<{ date: string; sales: number; orders: number; newCustomers: number }>;
  statusBreakdown: Array<{ status: string; count: number }>;
  orders: AdminRecord[];
  orderRows: number;
  truncated: boolean;
}

const baseUrl = import.meta.env.VITE_API_URL as string | undefined;
const tokenKey = 'km-admin-token';
const refreshKey = 'km-admin-refresh-token';
const roleKey = 'km-admin-role';
const getToken = () => globalThis.sessionStorage?.getItem(tokenKey) ?? '';
const getRefreshToken = () => globalThis.sessionStorage?.getItem(refreshKey) ?? '';

function clearSession(notify = true) {
  sessionStorage.removeItem(tokenKey);
  sessionStorage.removeItem(refreshKey);
  sessionStorage.removeItem('km-admin-name');
  sessionStorage.removeItem(roleKey);
  if (notify) globalThis.dispatchEvent?.(new Event('km-admin-session-expired'));
}

async function renewSession() {
  const refreshToken = getRefreshToken();
  if (!baseUrl || !refreshToken) throw new Error('انتهت جلسة الدخول.');
  const response = await fetch(baseUrl + '/auth/refresh', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || !body?.data?.accessToken) throw new Error('انتهت جلسة الدخول.');
  const permissions = Array.isArray(body.data.user?.permissions) ? body.data.user.permissions : [];
  if (!permissions.includes('*') && !permissions.includes('admin.access')) {
    clearSession(false);
    throw new Error('لم يعد هذا الحساب يملك صلاحية دخول لوحة الإدارة.');
  }
  sessionStorage.setItem(tokenKey, body.data.accessToken);
  if (body.data.refreshToken) sessionStorage.setItem(refreshKey, body.data.refreshToken);
  if (body.data.user?.name) sessionStorage.setItem('km-admin-name', body.data.user.name);
  if (body.data.user?.role) sessionStorage.setItem(roleKey, body.data.user.role);
  return body.data.accessToken as string;
}

async function sessionFetch(path: string, init?: RequestInit) {
  const send = (token: string) => fetch(baseUrl + path, {
    ...init,
    headers: {
      'content-type': 'application/json',
      authorization: 'Bearer ' + token,
      ...init?.headers,
    },
  });
  let response = await send(getToken());
  const sessionFailed = async (candidate: Response) => {
    if (candidate.status !== 401) return false;
    const body = await candidate.clone().json().catch(() => ({}));
    return ['INVALID_SESSION', 'UNAUTHORIZED', 'ACCOUNT_INACTIVE'].includes(body?.error?.code);
  };
  if (await sessionFailed(response) && getRefreshToken()) {
    try {
      response = await send(await renewSession());
    } catch {
      clearSession();
      throw new Error('انتهت جلسة الدخول. سجّل الدخول مرة أخرى.');
    }
  }
  if (await sessionFailed(response)) {
    clearSession();
    throw new Error('انتهت جلسة الدخول. سجّل الدخول مرة أخرى.');
  }
  return response;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  if (!baseUrl) throw new Error('عنوان الخادم غير مضبوط.');
  const token = getToken();
  if (!token) throw new Error('يجب تسجيل الدخول أولًا.');
  const response = await sessionFetch('/admin' + path, init);
  if (!response.ok) {
    if (response.status === 403 && path !== '/workspace') globalThis.dispatchEvent?.(new Event('km-admin-permissions-changed'));
    let message = 'تعذر تنفيذ العملية.';
    try {
      const body = await response.json();
      const details = Array.isArray(body?.error?.details) ? body.error.details : [];
      const fieldLabels: Record<string, string> = {
        name: 'الاسم', email: 'البريد الإلكتروني', phone: 'رقم الهاتف', password: 'كلمة المرور',
        role: 'الدور والصلاحية', branch: 'الفرع', active: 'حالة الحساب',
      };
      const detailMessage = details.map((detail: { path?: Array<string | number>; message?: string }) => {
        const field = String(detail.path?.[0] ?? '');
        return `${fieldLabels[field] || field || 'البيانات'}: ${detail.message || 'قيمة غير صحيحة.'}`;
      }).join('\n');
      message = detailMessage || body?.error?.message || message;
    } catch {
      message = 'تعذر الاتصال بالخادم.';
    }
    throw new Error(message);
  }
  if (response.status === 204) return undefined as T;
  const body = await response.json();
  return body.data as T;
}

export const managementApi = {
  configured: Boolean(baseUrl),
  authenticated: () => Boolean(getToken()),
  role: () => globalThis.sessionStorage?.getItem(roleKey) ?? '',
  siteSettings: async () => {
    if (!baseUrl) return { logoUrl: '/brand-logo-web.jpg', siteBackgroundUrl: '', heroBackgroundUrl: '', fontFamily: 'tajawal' as const, hideOutOfStockProducts: false, whatsapp: '', facebook: '', instagram: '', threads: '', telegram: '', branches: [] };
    const response = await fetch(baseUrl + '/site-settings');
    if (!response.ok) return { logoUrl: '/brand-logo-web.jpg', siteBackgroundUrl: '', heroBackgroundUrl: '', fontFamily: 'tajawal' as const, hideOutOfStockProducts: false, whatsapp: '', facebook: '', instagram: '', threads: '', telegram: '', branches: [] };
    const body = await response.json().catch(() => ({}));
    return body?.data as SiteSettings;
  },
  login: async (identifier: string, password: string) => {
    if (!baseUrl) throw new Error('عنوان الخادم غير مضبوط.');
    const response = await fetch(baseUrl + '/auth/login', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ identifier, password }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body?.error?.message || 'بيانات الدخول غير صحيحة.');
    const permissions = Array.isArray(body.data.user.permissions) ? body.data.user.permissions : [];
    if (!permissions.includes('*') && !permissions.includes('admin.access')) throw new Error('هذا الحساب لا يملك صلاحية دخول لوحة الإدارة.');
    sessionStorage.setItem(tokenKey, body.data.accessToken);
    sessionStorage.setItem(refreshKey, body.data.refreshToken);
    sessionStorage.setItem('km-admin-name', body.data.user.name);
    sessionStorage.setItem(roleKey, body.data.user.role);
    return body.data.user as AdminRecord;
  },
  logout: () => clearSession(false),
  changePassword: async (currentPassword: string, newPassword: string) => {
    if (!baseUrl) throw new Error('عنوان الخادم غير مضبوط.');
    if (!getToken()) throw new Error('يجب تسجيل الدخول أولًا.');
    const response = await sessionFetch('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body?.error?.message || 'تعذر تغيير كلمة المرور.');
    }
  },
  workspace: () => request<AdminWorkspace>('/workspace'),
  salesReport: (from: string, to: string) => request<SalesReport>(`/analytics/sales-report?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  save: (resource: string, id: string | undefined, payload: AdminRecord) =>
    request<AdminRecord>('/' + resource + (id ? '/' + id : ''), {
      method: id ? 'PATCH' : 'POST',
      body: JSON.stringify(payload),
    }),
  remove: (resource: string, id: string) =>
    request<void>('/' + resource + '/' + id, { method: 'DELETE' }),
  deleteProductPermanently: (id: string) =>
    request<void>('/products/' + id + '/permanent', { method: 'DELETE' }),
  updateOrder: (id: string, status: string) =>
    request<AdminRecord>('/orders/' + id + '/status', {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),
  addInventoryMovement: (payload: AdminRecord) =>
    request<AdminRecord>('/inventory/movements', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  saveSettings: (payload: SiteSettings) =>
    request<SiteSettings>('/settings', { method: 'PATCH', body: JSON.stringify(payload) }),
  uploadImage: async (file: File) => {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error('تعذر قراءة ملف الصورة.'));
      reader.readAsDataURL(file);
    });
    const data = dataUrl.slice(dataUrl.indexOf(',') + 1);
    return request<{ url: string; size: number }>('/uploads', {
      method: 'POST',
      body: JSON.stringify({ contentType: file.type, data }),
    });
  },
};
